/**
 * ServiceAdvisorRow Component
 *
 * Expandable row displaying a single service advisor's performance.
 * Collapsed: Shows name and total labor gross.
 * Expanded: Shows detailed breakdown by pay type.
 */

import React from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ServiceAdvisor, PayTypeMetrics } from './types';

interface ServiceAdvisorRowProps {
  advisor: ServiceAdvisor;
  isExpanded: boolean;
  onToggle: () => void;
  rank: number;
}

/**
 * Format currency value
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format number with decimals
 */
function formatNumber(value: number | null, decimals: number = 1): string {
  if (value === null || value === undefined) return '-';
  return value.toFixed(decimals);
}

/**
 * Pay type display configuration
 */
const PAY_TYPES: Array<{
  key: keyof Pick<ServiceAdvisor, 'customer' | 'warranty' | 'internal' | 'serviceContract'>;
  label: string;
  shortLabel: string;
}> = [
  { key: 'customer', label: 'Customer Pay', shortLabel: 'CP' },
  { key: 'warranty', label: 'Warranty', shortLabel: 'W' },
  { key: 'internal', label: 'Internal', shortLabel: 'I' },
  { key: 'serviceContract', label: 'Service Contract', shortLabel: 'SC' },
];

export const ServiceAdvisorRow: React.FC<ServiceAdvisorRowProps> = ({
  advisor,
  isExpanded,
  onToggle,
  rank,
}) => {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      {/* Main row - always visible */}
      <div
        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-gray-400 transition-transform duration-200">
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
          <span className="text-xs text-gray-400 font-medium w-5">{rank}</span>
          <span className="font-medium text-gray-900 truncate">
            {advisor.advisorName}
          </span>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <span className="text-lg font-bold text-orange-600">
              {formatCurrency(advisor.laborGrossTotal)}
            </span>
          </div>
          <div className="text-right text-sm w-24">
            <span className="text-gray-900">{advisor.roCountTotal}</span>
            <span className="text-gray-400"> / </span>
            <span className="text-blue-600">{advisor.openROCount ?? 0}</span>
            <span className="text-gray-400 text-xs ml-1">ROs</span>
          </div>
        </div>
      </div>

      {/* Expanded details - pay type breakdown */}
      {isExpanded && (
        <div className="ml-12 mr-3 mb-3 bg-gray-50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wider">
                <th className="text-left py-2 px-3 font-medium">Pay Type</th>
                <th className="text-right py-2 px-3 font-medium">ROs</th>
                <th className="text-right py-2 px-3 font-medium">Labor Gross</th>
                <th className="text-right py-2 px-3 font-medium">Hours</th>
                <th className="text-right py-2 px-3 font-medium">ELR</th>
                <th className="text-right py-2 px-3 font-medium">Hrs/RO</th>
              </tr>
            </thead>
            <tbody>
              {PAY_TYPES.map((payType) => {
                const metrics = advisor[payType.key] as PayTypeMetrics;
                // Only show rows with actual data
                if (metrics.roCount === 0 && metrics.laborGross === 0) {
                  return null;
                }
                return (
                  <tr
                    key={payType.key}
                    className="border-t border-gray-200 hover:bg-white"
                  >
                    <td className="py-2 px-3 text-gray-700">{payType.label}</td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {metrics.roCount}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900 font-medium">
                      {formatCurrency(metrics.laborGross)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {formatNumber(metrics.flatRateHours)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {metrics.elr !== null ? `$${formatNumber(metrics.elr, 2)}` : '-'}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {formatNumber(metrics.avgHoursPerRo, 2)}
                    </td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr className="border-t-2 border-gray-300 bg-gray-100 font-medium">
                <td className="py-2 px-3 text-gray-700">Total</td>
                <td className="py-2 px-3 text-right text-gray-900">
                  {advisor.roCountTotal}
                </td>
                <td className="py-2 px-3 text-right text-orange-600 font-bold">
                  {formatCurrency(advisor.laborGrossTotal)}
                </td>
                <td className="py-2 px-3 text-right text-gray-900">
                  {formatNumber(advisor.flatRateHoursTotal)}
                </td>
                <td className="py-2 px-3 text-right text-gray-900">
                  {advisor.elrTotal !== null
                    ? `$${formatNumber(advisor.elrTotal, 2)}`
                    : '-'}
                </td>
                <td className="py-2 px-3 text-right text-gray-900">
                  {formatNumber(advisor.avgHoursPerRoTotal, 2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
